import { useState } from "react";
import { SOLAR } from "@/constants/testIds";
import LocationMap from "@/components/LocationMap";
import PlaceSearch from "@/components/solar/PlaceSearch";
import CoordsForm from "@/components/solar/CoordsForm";
import { coordLabel } from "@/components/solar/ui";

type Mode = "place" | "coords" | "map";

const MODES: { id: Mode; label: string; testid: string }[] = [
  { id: "place", label: "Place name", testid: SOLAR.togglePlaceBtn },
  { id: "coords", label: "Coordinates", testid: SOLAR.toggleManualBtn },
  { id: "map", label: "Map", testid: SOLAR.toggleMapBtn },
];

function ModeToggle({ mode, onChange }: { mode: Mode; onChange: (m: Mode) => void }) {
  return (
    <div className="ml-auto inline-flex rounded-full border border-[#d9c9ad] p-1 text-sm">
      {MODES.map((m) => (
        <button
          key={m.id}
          data-testid={m.testid}
          onClick={() => onChange(m.id)}
          className={`rounded-full px-4 py-1.5 font-sans transition ${
            mode === m.id ? "bg-[#2b1f14] text-[#f6efe3]" : "text-[#5e4a35] hover:text-[#2b1f14]"
          }`}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

interface Props {
  lat: number;
  lon: number;
  tzOffsetMinutes: number;
  initialQuery: string;
  onLocation: (lat: number, lon: number, label: string) => void;
}

export default function LocationPanel({ lat, lon, tzOffsetMinutes, initialQuery, onLocation }: Props) {
  const [mode, setMode] = useState<Mode>("place");
  const [query, setQuery] = useState(initialQuery);

  const pickCoords = (la: number, lo: number) => onLocation(la, lo, coordLabel(la, lo));
  const fromDevice = (la: number, lo: number) => {
    pickCoords(la, lo);
    setMode("coords");
  };

  return (
    <section className="rounded-sm border border-[#d9c9ad] bg-[#fbf6ea] p-6 md:p-10">
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <h2 className="font-serif text-2xl">Where are you?</h2>
        <ModeToggle mode={mode} onChange={setMode} />
      </div>
      {mode === "place" && (
        <PlaceSearch query={query} onQueryChange={setQuery} onLocation={onLocation} onDeviceLocation={fromDevice} />
      )}
      {mode === "coords" && <CoordsForm lat={lat} lon={lon} onApply={pickCoords} onDeviceLocation={fromDevice} />}
      {mode === "map" && (
        <div className="flex flex-col gap-4">
          <p className="font-sans text-xs italic text-[#8a6f52]">
            Click or drag the pin to choose a place. The dashed vertical line is your time-zone's standard
            meridian — the longitude your clock is nominally keyed to.
          </p>
          <LocationMap latitude={lat} longitude={lon} tzOffsetMinutes={tzOffsetMinutes} onPick={pickCoords} />
        </div>
      )}
    </section>
  );
}
